/**
 * Declarative hint configuration — single source of truth for all onboarding hints.
 *
 * type:
 *   'dom'        — glow a CSS-selected DOM element, optionally intercept clicks
 *   'map-node'   — glow SVG map nodes in ProgressMap (handled via glowNodeTypes context)
 *   'page-enter' — auto-trigger discovery when user navigates to matching page
 *
 * gatedBy: array of hint IDs that must be discovered before this hint activates.
 * interceptClick: if true, first click shows discovery modal instead of normal behavior.
 * glowAll: if true, glow ALL matching elements (default: first match only).
 * targetIndex: 'middle' to glow the middle element among all matches.
 * glowClass: custom CSS class for glow (default: 'hint-glow').
 */

export const HINTS = [
  {
    id: 'sidebar_deck',
    title: 'Your Deck',
    body: 'Each uploaded lecture becomes a deck. Tap a deck to see its progression map: topics, objectives, and boss fights laid out as a path to mastery.',
    tokens: 20,
    xp: 10,
    icon: 'map-pin',
    category: 'map',
    onboarding: true,
    type: 'dom',
    // Exclude in-progress decks (`.tree-processing`) so the discovery
    // glow only fires once the deck is actually parsed and clickable.
    // Same hardening pattern as the create-exam glow at Dashboard.jsx.
    selector: '.tree-topic-header:not(.tree-processing)',
    page: '/dashboard',
    gatedBy: [],
    interceptClick: true,
    // Single-target: only the first parsed deck glows. "Any one" beats
    // "all of them" visually — sidebar with 58 pulsing rows was unusable.
    glowClass: 'hint-sidebar-glow',
    postAction: 'open-first-deck',
  },
  {
    id: 'add_exam',
    title: 'Create an Exam',
    body: 'Set an exam date and attach your decks. This builds a study map with progression nodes, boss fights, and a countdown to exam day.',
    tokens: 25,
    xp: 15,
    icon: 'target',
    category: 'progression',
    onboarding: true,
    type: 'dom',
    selector: '.ct-orb--exam',
    page: '/dashboard',
    gatedBy: [],
    interceptClick: true,
    glowAll: true,
    // Don't surface until the user has at least one parsed deck — there's
    // nothing to attach to an exam otherwise.
    requiresParsedDeck: true,
    postAction: 'open-exam-form',
  },
  {
    id: 'bank',
    title: 'Drug Bank',
    body: 'Every drug mentioned in your lectures is extracted and cataloged here. Master drug cards through flashcard drills to build your pharmacy knowledge base.',
    tokens: 30,
    xp: 15,
    icon: 'book',
    category: 'discovery',
    onboarding: true,
    type: 'dom',
    selector: '.player-tray .pt-orb:first-child',
    page: '/dashboard',
    gatedBy: [],
    revealAfterAction: true,
  },
  {
    id: 'subtopic',
    title: 'Topic Node',
    body: 'Red nodes are topics extracted from your lecture. Tap one to enter a teaching session: adaptive lessons that build understanding step by step.',
    tokens: 25,
    xp: 15,
    icon: 'map',
    category: 'map',
    onboarding: true,
    type: 'map-node',
    nodeTypes: ['topic'],
    page: '/dashboard',
    gatedBy: ['add_exam'],
    interceptClick: true,
  },
  {
    id: 'content',
    title: 'Content Diamond',
    body: 'Diamond nodes hold slide content: the key concepts and diagrams from your lecture, organized for quick review and deeper understanding.',
    tokens: 25,
    xp: 15,
    icon: 'shield',
    category: 'map',
    onboarding: true,
    type: 'map-node',
    nodeTypes: ['content'],
    page: '/dashboard',
    gatedBy: ['add_exam'],
    interceptClick: true,
  },
  {
    id: 'objective',
    title: 'Learning Objective',
    body: 'Purple nodes are your learning objectives: the skills your exam will test. Completing connected topics unlocks these for focused objective-level practice.',
    tokens: 25,
    xp: 15,
    icon: 'flag',
    category: 'progression',
    onboarding: true,
    type: 'dom',
    selector: '.objective-pair',
    page: '/deck/',
    gatedBy: [],
    interceptClick: true,
    targetIndex: 'middle',
  },
  {
    id: 'wand_button',
    title: 'Concept Builder',
    body: 'Highlight text on any slide to create concept cards: comparison tables, flowcharts, and summaries. Your personal study toolkit.',
    tokens: 25,
    xp: 15,
    icon: 'wand',
    category: 'tool',
    onboarding: true,
    type: 'dom',
    selector: '.bls-status-header--wand',
    page: ['/deck/', '/learn/', '/bank/', '/objective/'],
    gatedBy: ['objective'],
    interceptClick: true,
    postAction: 'activate-wand',
  },
  {
    id: 'fountain',
    title: 'Dialysis',
    body: 'Dialysis is a combat-style heal session. Your toxin pool equals the HP you\'re missing. Every correct answer drains it, wrong/IDK does nothing. Clear the pool and you\'re restored.',
    tokens: 30,
    xp: 20,
    icon: 'flask',
    category: 'combat',
    onboarding: true,
    type: 'map-node',
    nodeTypes: ['fountain', 'drill'],
    page: '/dashboard',
    gatedBy: ['add_exam'],
    interceptClick: false,
  },
  {
    id: 'boss',
    title: 'Boss Fight',
    body: 'The final test. Boss fights pull questions from across the entire deck. Defeat the boss to prove mastery, but watch your HP.',
    tokens: 40,
    xp: 25,
    icon: 'sword',
    category: 'combat',
    onboarding: true,
    type: 'map-node',
    nodeTypes: ['boss', 'final_boss'],
    page: '/dashboard',
    gatedBy: ['add_exam'],
    interceptClick: true,
  },

  // ── Wave 2: broader feature discovery ──────────────────────────

  {
    id: 'stats_hud',
    title: 'Player Stats',
    body: 'Your level, currency, and drug card progress at a glance. Click to open your full player profile: mastery breakdown, achievements, and daily income.',
    tokens: 15,
    xp: 10,
    icon: 'shield',
    category: 'tool',
    onboarding: true,
    type: 'dom',
    selector: '.stats-hud',
    page: null, // any page
    gatedBy: ['add_exam'],
    interceptClick: true,
  },
  {
    id: 'upload',
    title: 'Upload a Lecture',
    body: 'Start here. Drop a PowerPoint and Debono parses it into a study map: topics, learning objectives, drug cards, boss fights, concept cards. One lecture = one mission.',
    tokens: 25,
    xp: 15,
    icon: 'upload',
    category: 'tool',
    onboarding: true,
    type: 'dom',
    selector: '.bls-status-header--upload',
    page: '/dashboard',
    gatedBy: [],
    interceptClick: true,
    glowClass: 'hint-glow--tool',
    postIntakeOnly: true,
  },
  {
    id: 'train',
    title: 'Training Grounds',
    body: 'Game modes to master your drug cards. Speed Match for fast recall, Blind Recall for deep memory, Drug Showdown for clinical comparisons. Mastering cards earns you daily income.',
    tokens: 25,
    xp: 15,
    icon: 'crown',
    category: 'progression',
    onboarding: true,
    type: 'page-enter',
    page: '/train',
    gatedBy: ['bank'],
  },
  {
    id: 'drug_card',
    title: 'Drug Cards',
    body: 'Every drug from your lectures, auto-extracted. Cards progress from Inactive to Active to Mastered as you drill them. Flip a card to test yourself on the back. Mastered cards earn daily currency.',
    tokens: 20,
    xp: 10,
    icon: 'book',
    category: 'discovery',
    onboarding: true,
    type: 'dom',
    selector: '.drug-card-container',
    page: '/bank',
    gatedBy: ['bank'],
    interceptClick: true,
  },
  // Post-intake area-pick hints. The dashboard auto-loads the picked
  // DLC's map (sessionStorage seed in markIntakeCompleted activates
  // naplexMode/ptcbMode/mpjeMode on Dashboard mount). These hints fire
  // ON THE DASHBOARD with the DLC map open, glowing each area-row in
  // the side panel and showing a toast that tells the user a single
  // pick is free. Clicking any area marks the hint seen.
  // Selector covers the dashboard's review-area rows in the
  // BottomLeftStatusRail/ContentTray review panel; if the dashboard
  // ever renders areas under a different class, broaden / refine here.
  {
    id: 'naplex_area_pick',
    title: 'Pick One Therapeutic Area, Free',
    body: 'Pick any one therapeutic area on this map. We add it to your game completely free: full lessons, drug cards, boss fights, the works. The full DLC unlocks the other 14 later.',
    tokens: 20,
    xp: 10,
    icon: 'target',
    category: 'dlc',
    onboarding: true,
    type: 'dom',
    selector: '[data-naplex-area], .naplex-area-row, .review-area-row, .naplex-card',
    page: '/dashboard',
    gatedBy: [],
    requiresInterest: 'naplex',
    glowClass: 'hint-dlc-glow',
    glowAll: true,
    markSeenOnClick: true,
    toast: 'Pick any therapeutic area on the map. We seed it free.',
  },
  {
    id: 'ptcb_area_pick',
    title: 'Pick One Domain, Free',
    body: 'Pick any one PTCE domain on this map. We add it to your game completely free: drills, station scenarios, workflow boss. The full DLC unlocks the other three later.',
    tokens: 20,
    xp: 10,
    icon: 'target',
    category: 'dlc',
    onboarding: true,
    type: 'dom',
    selector: '[data-ptcb-area], .ptcb-area-row, .review-area-row, .ptcb-card, .naplex-card',
    page: '/dashboard',
    gatedBy: [],
    requiresInterest: 'ptcb',
    glowClass: 'hint-dlc-glow',
    glowAll: true,
    markSeenOnClick: true,
    toast: 'Pick any domain on the map. We seed it free.',
  },
  {
    id: 'mpje_area_pick',
    title: 'Pick One Area, Free',
    body: 'Pick any one area on this map (federal core or your state). We add it to your game completely free: citation-tracked drills, inspector scenarios, statute map. The full DLC unlocks the rest later.',
    tokens: 20,
    xp: 10,
    icon: 'target',
    category: 'dlc',
    onboarding: true,
    type: 'dom',
    selector: '[data-mpje-area], .mpje-area-row, .review-area-row, .mpje-card, .naplex-card',
    page: '/dashboard',
    gatedBy: [],
    requiresInterest: 'mpje',
    glowClass: 'hint-dlc-glow',
    glowAll: true,
    markSeenOnClick: true,
    toast: 'Pick any area on the map. We seed it free.',
  },
  {
    id: 'achievement',
    title: 'Achievements',
    body: 'Milestones that reward you for hitting progression goals: first boss kill, card mastery streaks, level thresholds. Each one pays out bonus currency or XP.',
    tokens: 15,
    xp: 10,
    icon: 'trophy',
    category: 'discovery',
    onboarding: false,
    type: 'dom',
    selector: '.bls-status-header--ach',
    page: '/dashboard',
    gatedBy: ['add_exam'],
    interceptClick: true,
  },
];

/** Ordered list of all hint IDs */
export const HINT_IDS = HINTS.map(h => h.id);

/** Content-dependent hints — cleanup triggers when all are discovered */
export const CONTENT_HINT_IDS = ['sidebar_deck', 'subtopic', 'content', 'objective', 'fountain', 'boss'];

/** Quick lookup: id -> hint config */
export const HINT_MAP = Object.fromEntries(HINTS.map(h => [h.id, h]));

/**
 * Map node type -> hint ID for ProgressMap glow + click interception.
 * Includes 'trial' -> 'objective' mapping (trial nodes trigger the objective hint).
 */
export const NODE_TYPE_TO_HINT = {
  topic: 'subtopic',
  trial: 'objective',
  content: 'content',
  // fountain/drill click-interception removed — dialysis-as-combat session
  // launches directly without a discovery modal. The 'fountain' hint config
  // entry still exists for any other discovery surfacing, but Dashboard's
  // click handler no longer blocks fountain/drill nodes on it.
  boss: 'boss',
  final_boss: 'boss',
};
